import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiError } from "../context/AuthContext";
import api from "../lib/api";
import { ShieldCheck } from "lucide-react";

const field = "w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-600";

export default function ChangePassword() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const forced = user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next !== confirm) return setErr("New passwords do not match.");
    if (next.length < 10) return setErr("Password must be at least 10 characters.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Password updated.");
      nav("/");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-slate-50 p-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-teal-700">
          <ShieldCheck size={20} />
          <span className="font-display text-sm font-bold uppercase tracking-widest">CensusPulse</span>
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Change your password</h2>
        <p className="mt-1 text-sm text-slate-500">
          {forced
            ? "Your account uses a temporary password. Set a new password to continue."
            : "Update your account password."}
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input data-testid="cp-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Current (temporary) password" className={field} />
          <input data-testid="cp-new" type="password" required minLength={10} value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password (min 10 chars)" className={field} />
          <input data-testid="cp-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className={field} />
          {err && <div data-testid="cp-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700">{err}</div>}
          <button data-testid="cp-submit" type="submit" disabled={busy} className="w-full rounded-lg bg-teal-700 py-3 text-sm font-bold text-white hover:bg-teal-800 disabled:opacity-60">
            {busy ? "Please wait…" : "Update password"}
          </button>
          <button type="button" data-testid="cp-signout" onClick={async () => { await logout(); nav("/login"); }} className="block w-full text-center text-sm font-semibold text-slate-500 hover:underline">
            Sign out
          </button>
        </form>
      </div>
    </div>
  );
}
